<template>
  <div class="bbp-date-picker" v-click-outside="hideCalendar">
    <v-date-picker
      color="blue"
      :mode='modePicker'
      :popover="{
        positionFixed: true,
        visibility: disabled ? 'hidden' : 'click',
        placement: isScreens ? $screens({md: 'auto'}) : undefined
      }"
      v-model='modelDate'
      :minDate="getMinDate()"
      :maxDate="getMaxDate()"
    >
      <div
        :class="[
          'bbp-input',
          'bbp-button_border',
          'bbp-date-picker-body',
          styleType && `bbp-date-picker-body_${styleType}`,
        ]"
        @click="openCalendar = disabled ? false : !openCalendar"
      >
        <div
          v-if="modePicker === 'single'"
          class="bbp-date-picker-body__date"
        >
          {{formatSingleDate}}
        </div>
        <div
          v-else
          class="bbp-date-picker-body-range"
        >
          <div class="bbp-date-picker-body__date">{{formatStartDate}}</div>
          <div class="bbp-date-picker-body__separator">-</div>
          <div class="bbp-date-picker-body__date">{{formatEndDate}}</div>
        </div>
        <i
          :class="[
            'bbp-icon-calendar',
            'bbp-date-picker-body__icon',
            styleType && `bbp-date-picker-body__icon_${styleType}`
          ]"
        />
      </div>
    </v-date-picker>
  </div>
</template>

<script>
import moment from 'moment'
import {get} from 'lodash'

export default {
  name: 'BBPDatePicker',
  props: ['input', 'model', 'disabled', 'styleType'],
  data () {
    return {
      openCalendar: false,
      selectedDate: null,
      rangeDates: {
        start: '',
        end: ''
      }
    }
  },
  computed: {
    isScreens () {
      return get(this.input, 'isScreens', false)
    },
    modelDate: {
      get () {
        return this.modePicker === 'single' ? this.selectedDate : this.rangeDates
      },
      set (value) {
        const defaultValue = !value ? this.modelDate : value
        this.modePicker === 'single' ? this.selectedDate = defaultValue : this.rangeDates = defaultValue
      }
    },
    modePicker () {
      return get(this.input, 'mode', 'single')
    },
    formatSingleDate () {
      const format = get(this.input, 'format', 'DD-MMM-YY')
      return this.formatDate(this.selectedDate, format)
    },
    formatStartDate () {
      const format = get(this.input, 'format', 'DD-MMM-YY')
      const start = get(this.model, 'dates.start', '')
      return this.formatDate(start, format)
    },
    formatEndDate () {
      const format = get(this.input, 'format', 'DD-MMM-YY')
      const end = get(this.model, 'dates.end', '')
      return this.formatDate(end, format)
    }
  },
  methods: {
    getMinDate () {
      const { minDate } = this.input
      if (minDate) return minDate
      return false
    },
    getMaxDate () {
      const { maxDate } = this.input
      if (maxDate) return maxDate
      return false
    },
    formatDate (date, format) {
      const momentDate = moment(date)
      const validDate = momentDate.isValid()
      return validDate ? momentDate.format(format) : ''
    },
    hideCalendar () {
      this.openCalendar = false
    },
    setDefaultSingleDate () {
      this.selectedDate = get(this.model, this.input.model, null)
    },
    setDefaultRangeDate () {
      this.rangeDates = get(this.model, this.input.model, {})
    },
    setDefaultDate () {
      this.modePicker === 'range' ? this.setDefaultRangeDate() : this.setDefaultSingleDate()
    }
  },
  watch: {
    selectedDate () {
      this.$emit('input', this.selectedDate)
    },
    rangeDates () {
      const { isFull = false } = this.input
      if (isFull) {
        this.$emit('input', this.rangeDates, this.model, this.input)
      } else {
        this.$emit('input', this.rangeDates)
      }
    },
    model () {
      this.setDefaultDate()
    }
  },
  beforeMount () {
    this.setDefaultDate()
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../../sass/variables";
  @import "../../../../sass/mixins";

  .bbp-date-picker {
    @include global-font;
    cursor: pointer;

    &-body {
      display: inline-grid;
      align-items: center;
      grid-template-columns: 1fr 14px;
      height: 100%;
      padding: 7px 8px;
      border: 1px solid $color-mercury;

      &_only-icon {
        display: flex;
        border: none;
      }

      &_table-filter {
        font-size: inherit;
        grid-template-columns: 1fr 42px;
        padding: 0;
        border: none;
      }

      &_mini {
        border: none;
        grid-template-columns: 14px 1fr;
        grid-gap: 0 5px;
        padding: 0;
      }

      &:hover &__date{
        opacity: 0.8;
      }

      &:hover &__icon{
        color: $color-accent-hover;
      }

      &__date {
        color: $color-black;
        transition: 0.3s;
      }

      &__separator {
        color: $color-black;
        margin: 0 4px;
      }

      &-range {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100%;
        border-right: 1px solid $color-alabaster;
      }

      &__icon {
        color: $color-accent;
        transition: 0.3s;

        &_table-filter {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
        }

        &_mini {
          grid-column: 1;
          grid-row: 1;

          &:before {
            margin-right: 0;
            margin-left: 0;
          }
        }
      }
    }
  }

</style>

<style lang="scss">
  @import "../../../../sass/variables";
  @import "../../../../sass/mixins";

  .bbp-date-picker {
    .vc-popover-content-wrapper {
      z-index: 90;
    }

    .vc-text-gray-500 {
      @include global-font;
      color: rgba($color-accent, 0.54);
    }

    .vc-rounded-full {
      border-radius: 4px;
    }

    .vc-rounded-lg {
      border-right: 10px;
    }

    .vc-border {
      border: none;
      box-shadow: 0 4px 20px rgba($color-accent, 0.1);
    }

    .vc-popover-caret {
      z-index: 2;
    }

    .vc-bg-gray-800 {
      background-color: $color-mariner;
    }

    .hover\:vc-bg-gray-900:hover {
      background-color: $color-accent-hover;
    }

    .hover\:vc-shadow-inner:hover {
      box-shadow: none;
    }

    .vc-text-gray-600,
    .vc-text-gray-800,
    .vc-text-gray-900 {
      @include global-font;
      color: $color-accent;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Main/BBPForm/BBPDatePicker/BBPDatePicker.vue